(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else root.createTKCartSnapshotCapture = factory;
})(globalThis, function createTKCartSnapshotCapture(options, env) {
  'use strict';
  options = options || {};
  env = env || globalThis;
  var METRIC = 'TK Added to Cart Snapshot';
  var revision = 0;
  var lastSentSignature = null;
  var pendingSignature = null;
  var allowedReasons = ['paid_add', 'paid_change'];
  var origin = new URL(options.origin || env.location.origin).origin;
  if (origin.indexOf('https://') !== 0) throw new Error('HTTPS storefront required');
  if (typeof options.isSettledCart !== 'function' || typeof options.isGiftLine !== 'function') {
    throw new Error('Verified cart and managed-gift predicates are required');
  }

  function requireInt(value, name, minimum) {
    if (!Number.isSafeInteger(value) || value < minimum) throw new Error('Invalid ' + name);
    return value;
  }
  function text(value, maximum) { return typeof value === 'string' ? value.slice(0, maximum) : ''; }
  function money(cents) { return requireInt(cents, 'money', 0) / 100; }
  function publicUrl(value, isProduct) {
    if (typeof value !== 'string' || !value.trim()) return '';
    try {
      var url = new URL(value, origin);
      if (url.protocol !== 'https:' || url.username || url.password) return '';
      if (isProduct && (url.origin !== origin || !/\/products\//.test(url.pathname))) return '';
      return url.href;
    } catch (_) { return ''; }
  }
  function productUrl(item) {
    return publicUrl(item.url || ('/products/' + encodeURIComponent(item.handle || '')), true);
  }
  function imageUrl(item) {
    return publicUrl(item.featured_image && item.featured_image.url || item.image || '', false);
  }
  function row(item, isGift, parent) {
    var quantity = requireInt(item.quantity, 'quantity', 1);
    var finalLine = requireInt(item.final_line_price, 'final line price', 0);
    if (isGift && finalLine !== 0) throw new Error('Gift price has not settled');
    var title = text(item.product_title || item.title, 300);
    if (!title) throw new Error('Missing product title');
    var result = {
      product_id: String(requireInt(Number(item.product_id), 'product ID', 1)),
      variant_id: String(requireInt(Number(item.variant_id || item.id), 'variant ID', 1)),
      title: title,
      variant_title: text(item.variant_title, 180),
      quantity: quantity,
      image_url: imageUrl(item),
      product_url: productUrl(item),
      original_unit_price: money(item.original_price),
      final_unit_price: money(item.final_price),
      original_line_price: money(item.original_line_price),
      final_line_price: money(finalLine),
      is_gift: Boolean(isGift),
      is_complimentary: Boolean(isGift && finalLine === 0),
      gift_evidence: isGift ? (parent ? 'managed_parent_and_component_zero' : 'managed_line_zero') : ''
    };
    if (parent) result.parent_variant_id = String(parent.variant_id || parent.id);
    return result;
  }
  function giftComponentRow(component, parent) {
    // Component identity, quantity and image must come from the actual bundle.
    // Missing allocations may use the verified zero-priced managed parent;
    // an explicitly charged component is never relabelled as complimentary.
    var derived = component.final_line_price == null || component.final_price == null;
    if ((component.final_line_price != null && component.final_line_price !== 0)
        || (component.final_price != null && component.final_price !== 0)) throw new Error('Charged component allocation');
    var child = row(Object.assign({}, component, {
      final_line_price: component.final_line_price == null ? 0 : component.final_line_price,
      final_price: component.final_price == null ? 0 : component.final_price,
      original_price: component.original_price == null ? 0 : component.original_price,
      original_line_price: component.original_line_price == null ? 0 : component.original_line_price
    }), true, parent);
    if (!child.image_url) throw new Error('Missing component image');
    if (component.original_price == null) child.original_unit_price = null;
    if (component.original_line_price == null) child.original_line_price = null;
    if (derived) child.gift_evidence = 'managed_bundle_parent_zero';
    return child;
  }
  function hasProperties(item) {
    var mailMetadata = ['_tk_mail_final_line_price', '_tk_mail_quantity', '_tk_mail_is_gift',
      '_tk_mail_snapshot_version', '_tk_mail_snapshot_at', '_tk_mail_snapshot_id'];
    return Object.keys(item.properties || {}).some(function (key) {
      return mailMetadata.indexOf(key) === -1 && item.properties[key] !== null && item.properties[key] !== '';
    });
  }
  function build(cart, reason) {
    if (allowedReasons.indexOf(reason) === -1) throw new Error('Not a paid cart mutation');
    if (!cart || !Array.isArray(cart.items) || cart.currency !== 'USD') throw new Error('Unsupported cart');
    if (!options.isSettledCart(cart)) throw new Error('Cart is not verified and settled');
    requireInt(cart.total_price, 'cart total', 0);
    var displayRows = [];
    var paidRows = [];
    var finalLineTotal = 0;
    cart.items.forEach(function (item) {
      var isGift = options.isGiftLine(item) === true;
      if (item.properties && item.properties._tk_launch_gift && !isGift) throw new Error('Unrecognized managed gift');
      var normalized = row(item, isGift);
      finalLineTotal += item.final_line_price;
      if (!isGift) {
        // Variant-only permalinks cannot faithfully reconstruct these lines.
        // Do not drop their properties or send a falsely matching recovery link.
        if (hasProperties(item) || item.selling_plan_allocation || item.selling_plan) {
          throw new Error('Cart recovery cannot preserve line customization or selling plan');
        }
        paidRows.push(normalized);
      }
      var components = item.item_components;
      var expanded = null;
      if (isGift && Array.isArray(components) && components.length) {
        try {
          expanded = components.map(function (component) {
            return giftComponentRow(component, item);
          });
        } catch (_) {
          // Keep the actual bundle when component identity/image evidence is
          // incomplete or an explicit child allocation conflicts with free.
          expanded = null;
        }
      }
      var hasChargedComponent = Array.isArray(components) && components.some(function (component) {
        return component.final_line_price > 0 || component.final_price > 0;
      });
      if (isGift && (!expanded || expanded.length !== 2) && !hasChargedComponent && item.quantity === 1
          && typeof options.resolveGiftComponents === 'function') {
        var catalog = options.resolveGiftComponents(item);
        if (catalog && String(catalog.parent_variant_id) === normalized.variant_id
            && catalog.parent_quantity === 1 && catalog.evidence === 'managed_parent_catalog_verified'
            && Array.isArray(catalog.components) && catalog.components.length === 2) {
          try {
            expanded = catalog.components.map(function (component) {
              if (component.quantity !== 1) throw new Error('Unsupported catalog bundle quantity');
              var child = giftComponentRow(component, item);
              child.gift_evidence = 'managed_parent_catalog_verified';
              return child;
            });
            if (expanded[0].variant_id === expanded[1].variant_id) expanded = null;
          } catch (_) { expanded = null; }
        }
      }
      displayRows = displayRows.concat(expanded || [normalized]);
    });
    if (displayRows.length > 50) throw new Error('Cart exceeds candidate row limit');
    if (cart.total_price > finalLineTotal) throw new Error('Inconsistent cart total');
    var paidQuantity = paidRows.reduce(function (sum, item) { return sum + item.quantity; }, 0);
    var discountCodes = typeof options.recoveryDiscountCodes === 'function' ? options.recoveryDiscountCodes(cart) : [];
    if (!Array.isArray(discountCodes) || discountCodes.some(function (code) {
      return typeof code !== 'string' || !code || code.indexOf(',') !== -1 || code.length > 100;
    })) throw new Error('Unsupported recovery discount');
    var counts = {};
    paidRows.forEach(function (item) { counts[item.variant_id] = (counts[item.variant_id] || 0) + item.quantity; });
    var rebuild = '';
    if (paidQuantity) {
      var parts = Object.keys(counts).sort().map(function (id) { return id + ':' + counts[id]; });
      var recovery = new URL('/cart/' + parts.join(','), origin);
      recovery.searchParams.set('storefront', 'true');
      if (discountCodes.length) recovery.searchParams.set('discount', discountCodes.join(','));
      rebuild = recovery.href;
      if (rebuild.length > 2000) throw new Error('Recovery URL too long');
    }
    return {
      CartSnapshotVersion: 1,
      CartSnapshotReady: Boolean(paidQuantity),
      SnapshotValid: Boolean(paidQuantity),
      SnapshotReason: reason,
      Currency: cart.currency,
      CartItems: paidQuantity ? displayRows : [],
      CartRebuildURL: rebuild,
      PaidItems: paidRows.map(function (item) { return { variant_id: item.variant_id, quantity: item.quantity }; }),
      PaidQuantity: paidQuantity,
      PaidItemCount: paidRows.length,
      PaidSubtotal: paidRows.reduce(function (sum, item) { return sum + item.final_line_price; }, 0),
      CartTotal: money(cart.total_price),
      CartDiscountTotal: money(finalLineTotal - cart.total_price),
      '$value': money(cart.total_price),
      RecoveryDiscountCodes: discountCodes
    };
  }
  function consentAllowed() {
    try {
      var privacy = env.Shopify && env.Shopify.customerPrivacy;
      return Boolean(privacy && privacy.analyticsProcessingAllowed() === true && privacy.marketingAllowed() === true);
    } catch (_) { return false; }
  }
  function noteMutation(reason) {
    if (allowedReasons.indexOf(reason) === -1) throw new Error('Unsupported mutation reason');
    return { revision: ++revision, reason: reason };
  }
  function current(ticket) { return ticket && ticket.revision === revision; }
  async function captureSettled(cart, ticket) {
    if (!current(ticket)) return { sent: false, reason: 'superseded_mutation' };
    if (!consentAllowed()) return { sent: false, reason: 'consent_unavailable_or_denied' };
    var sdk = env.klaviyo;
    if (!sdk || typeof sdk.isIdentified !== 'function' || typeof sdk.track !== 'function') {
      return { sent: false, reason: 'klaviyo_unavailable' };
    }
    var payload;
    try { payload = build(cart, ticket.reason); }
    catch (error) { return { sent: false, reason: 'unsupported_or_unsettled_cart', detail: error.message }; }
    // This in-page fingerprint excludes timestamp/ID. No cookies, cart token,
    // email, recipient properties, or local/session storage are inspected.
    var signature = JSON.stringify(payload);
    if (signature === lastSentSignature || signature === pendingSignature) return { sent: false, reason: 'duplicate_snapshot' };
    try {
      if (await sdk.isIdentified() !== true) return { sent: false, reason: 'anonymous' };
      if (!current(ticket) || !consentAllowed()) return { sent: false, reason: 'state_changed_before_track' };
      if (signature === lastSentSignature || signature === pendingSignature) return { sent: false, reason: 'duplicate_snapshot' };
      if (!env.crypto || typeof env.crypto.randomUUID !== 'function') return { sent: false, reason: 'snapshot_id_unavailable' };
      payload.SnapshotID = env.crypto.randomUUID();
      payload.$event_id = payload.SnapshotID;
      payload.SnapshotCreatedAt = new Date().toISOString();
      pendingSignature = signature;
      var accepted = await sdk.track(METRIC, payload);
      if (accepted === true) lastSentSignature = signature;
      return { sent: accepted === true, reason: accepted === true ? 'sdk_accepted' : 'sdk_not_accepted',
        persistence: 'unverified', snapshotID: payload.SnapshotID };
    } catch (_) { return { sent: false, reason: 'sdk_error' }; }
    finally { if (pendingSignature === signature) pendingSignature = null; }
  }
  return { metric: METRIC, noteMutation: noteMutation, captureSettled: captureSettled, build: build };
});

